import React, { useState } from 'react';
import {
  SparklesIcon,
  InformationCircleIcon,
  AcademicCapIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';
import GradientCard from '../gamification/GradientCard';
import { useAISettings, useUpdateAISettings } from '../../services/aiCourses';
import { parseApiDate } from '../../utils/apiDates';

/**
 * Settings → AI: the per-workspace on-switch for AI features (Teri assistant
 * replies, AI course generation). Backed by GET/PUT /settings/ai; enabling
 * records consent (timestamp + actor) server-side. `available` is the
 * platform-level master switch — when it's off the toggle is locked and we
 * say so instead of rendering a switch that silently does nothing.
 */

const AI_FEATURES: { icon: React.ElementType; label: string; description: string }[] = [
  {
    icon: AcademicCapIcon,
    label: 'AI course creation',
    description: 'Generate a draft course from a topic, reference links, or your documents.',
  },
  {
    icon: ChatBubbleLeftRightIcon,
    label: 'Teri assistant',
    description: 'Let Teri answer questions about the platform and your workspace.',
  },
];

const AISettings: React.FC = () => {
  const { data: settings, isLoading } = useAISettings();
  const updateSettings = useUpdateAISettings();
  const [confirming, setConfirming] = useState(false);

  const available = !!settings?.available;
  const enabled = !!settings?.enabled;
  const busy = isLoading || updateSettings.isPending;

  const onToggle = () => {
    if (!available || busy) return;
    if (enabled) {
      updateSettings.mutate(false);
      setConfirming(false);
    } else {
      // Enabling shares tenant content with external AI providers — make the
      // consent explicit instead of a one-click accident.
      setConfirming(true);
    }
  };

  const confirmEnable = () => {
    updateSettings.mutate(true, { onSettled: () => setConfirming(false) });
  };

  return (
    <section className="max-w-2xl space-y-6">
      <header>
        <h2 className="text-xl font-bold text-text-primary">AI features</h2>
        <p className="mt-1 text-sm text-text-secondary">
          Turn AI-powered tools on or off for everyone in this workspace.
        </p>
      </header>

      <GradientCard className="p-6" overlayOpacity="opacity-20 dark:opacity-25">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <SparklesIcon className="h-4 w-4 shrink-0 text-primary-500" aria-hidden="true" />
              <p className="text-sm font-medium text-text-primary">Enable AI features</p>
            </div>
            <p className="mt-0.5 text-xs text-text-secondary">
              {available
                ? 'Applies to the whole workspace. You can turn this off at any time.'
                : 'AI features are not activated for this platform environment yet.'}
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={enabled}
            aria-disabled={!available || busy}
            aria-label="Enable AI features"
            onClick={onToggle}
            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors duration-fast ${
              enabled ? 'bg-primary-500' : 'bg-grey-300 dark:bg-grey-700'
            } ${!available || busy ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
          >
            <span
              className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform duration-fast ${
                enabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {confirming && (
          <div
            className="mt-4 rounded-lg border border-primary-500/30 bg-primary-500/5 p-4"
            data-testid="ai-consent"
          >
            <p className="text-xs leading-relaxed text-text-secondary">
              When AI features run, relevant workspace content (like course topics, reference
              documents, and questions asked to Teri) is processed by our AI providers to
              generate results. By enabling, you confirm you&apos;re authorized to accept this
              for your organization.
            </p>
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                onClick={confirmEnable}
                disabled={busy}
                className="rounded-full bg-primary-500 px-4 py-1.5 text-xs font-semibold transition-colors duration-fast hover:bg-primary-600 dark:hover:bg-primary-400 disabled:opacity-60"
              >
                I agree — enable AI
              </button>
              <button
                type="button"
                onClick={() => setConfirming(false)}
                className="rounded-full px-4 py-1.5 text-xs font-semibold text-text-secondary transition-colors duration-fast hover:text-text-primary"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {enabled && settings?.consent_at && (
          <p className="mt-3 flex items-center gap-1.5 text-xs text-text-tertiary">
            <InformationCircleIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
            Enabled {parseApiDate(settings.consent_at).toLocaleDateString()} — content may be shared
            with AI providers while features run.
          </p>
        )}
      </GradientCard>

      <GradientCard className="p-6" overlayOpacity="opacity-20 dark:opacity-25">
        <p className="mb-4 text-xs font-semibold uppercase tracking-wide text-text-secondary">
          What this unlocks
        </p>
        <div className="divide-y divide-border-primary/15">
          {AI_FEATURES.map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.label} className="flex items-start gap-3 py-4 first:pt-0 last:pb-0">
                <Icon className="mt-0.5 h-5 w-5 shrink-0 text-primary-500" aria-hidden="true" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-text-primary">{f.label}</p>
                  <p className="mt-0.5 text-xs text-text-secondary">{f.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </GradientCard>
    </section>
  );
};

export default AISettings;
